import React from 'react';
import { DropDownButtonItemClickEvent } from "@progress/kendo-react-buttons";
import {
  Component,
  PuzzleButton,
  PuzzleDropDownButton,
  Container
} from 'puzzle';
import { ColorPicker, ColorPickerChangeEvent } from "@progress/kendo-react-inputs";
import classNames from "classnames";
import _, { omit } from 'lodash';
import { getColorPalette, PALETTE } from "../../util/Util";
import { FILTER, FILTERS_LIST_ITEM_PROPS, MENU_ITEMS, FILTER_ABSTRACT_STATE } from "../../Dashboard/Filters/Types";

class FilterListItemAbstract<P = {}, S extends Partial<FILTER_ABSTRACT_STATE>= {}> extends Component<FILTERS_LIST_ITEM_PROPS & P, FILTER_ABSTRACT_STATE & S> {
  colorChangeAction = 'saveFilterAfterColorChange';
  countProperty: 'submissionCount' | 'jobPhaseCount' = 'submissionCount';
  updateDialogSavedSearches = false;
  supplierView = false;
  colorPalette = getColorPalette();
  paletteSettings = {
    palette: _.values(this.colorPalette).map((item: string) => {
      if (item === 'rgba(255, 255, 255, 0)') {
        item = 'transparent';
      }
      return item;
    }),
    columns: 3,
    tileSize: 20
  };

  state = {
    additionalLiClassName: '',
    showColorPalette: false
  };

  /**
   * Menu items attached to each filter, order is important because handleItemClick function depends on it
   */
  ITEMS: MENU_ITEMS = [
    { text: this.translate('clone') },
    { text: this.translate('edit') },
    { text: this.translate('delete') }
  ];

  /**
   * Selects color, hides palette and send ajax to server
   *
   * @param event
   */
  onColorPickerChange = (event: ColorPickerChangeEvent) => {
    let selectedColor = _.invert(this.colorPalette)[event.value];
    let updatedState: any = { showColorPalette: false };
    let currentItem = this.props.currentItem;
    if (currentItem.color !== selectedColor) {
      const searchData = currentItem.searchData;
      const filterData: any = omit(currentItem, ['filterId', this.countProperty]);
      filterData.color = selectedColor;
      filterData.jsonSearch = searchData;
      this.dispatch(this.colorChangeAction, '', {
        filterData: filterData,
        filterId: currentItem.filterId,
        updateDialogSavedSearches: this.updateDialogSavedSearches,
      })
    }
    this.setState(updatedState);
  };

  /**
   * Opens color palette and focus its picker
   */
  openColorPalette = () => {
    this.setState({ showColorPalette: true }, () => {
      let elementsByClassName = document.getElementsByClassName('k-colorpalette');
      let pickerPalette: any = elementsByClassName[elementsByClassName.length - 1];
      pickerPalette.focus();
    });
  };

  /**
   * Handles enter on filter box
   *
   * @param e
   */
  handleEnter = (e: any) => {
    const { keyCode } = e;
    const { currentItem } = this.props;
    const keyUp = 13;
    const space = 32;
    const onlyOnThisClass = 'tp-filter-list-item';

    if (onlyOnThisClass.includes(e.target.className) && (keyCode === keyUp || keyCode === space)) {
      delete currentItem.color;
      this.handleEnterFromParent(currentItem)
    }
  }

  /**
   * Handles enter on filter box
   * Has to be overridden in inherited class
   *
   * @param currentItem
   */
  handleEnterFromParent = (currentItem: FILTER) => {
    console.error('this method has to be overridden')
  }

  /**
   * Handles click on DropDownMenuItem based on it's position inside DropDownMenu
   * Has to be overridden in inherited class
   *
   * @param itemData
   * @param event
   */
  public handleItemClick = (itemData: any, event: DropDownButtonItemClickEvent) => {
    console.error('this method has to be overridden')
  };

  getAriaLabel = (color: string): string => {
    return this.translate('SRL-favoriteFilter') + ' ' + this.translate(`SRL-colorPalette.${color}`);
  }

  /**
   * Returns actions
   */
  renderActions = () => {
    return (
      <PuzzleDropDownButton
        buttonAriaLabel={this.translate('SRL-ButtonMenuText')}
        fillMode={'flat'}
        iconClass="tp-icon tp-more-vertical"
        items={this.ITEMS}
        onItemClick={this.handleItemClick.bind(this, this.props.currentItem)}
      />
    )
  };

  /**
   * Render for search name and count
   */
  public renderSearchNameWithCount = () => {
    const {
      submissionCount,
      filterName
    } = this.props.currentItem;
    return (
      <Container
        alignItemsCenter
        displayFlex
        flex1
        className={'tp-filter-list-item-wrap tp-100'}
      >
        {
          _.isUndefined(submissionCount) ?
            <i className={'tp-icon tp-icon-sm tp-loop'} aria-hidden={"true"} />
            :
            <div className="tp-filter-list-item-count tp-font-size-sm tp-text-medium"
                 tabIndex={-1}
            >
              {submissionCount}
            </div>
        }

        <div className="tp-filter-list-item-name" tabIndex={-1}>{filterName}</div>
      </Container>)
  }

  renderBody() {
    const { color = 'no_color' } = this.props.currentItem;
    const colorPalette: PALETTE = getColorPalette();
    const colorRgbString = colorPalette[color] || color;
    const paletteIndicator = classNames(
      'tp-filter-list-item-indicator',
      {
        'tp-transparent-indicator': color === 'no_color'
      }
    );
    return (
      <Container
        htmlTag={'li'}
        displayFlex
        className={`tp-filter-item-wrap tp-100 ${this.state.additionalLiClassName}`}
        ariaLabel={this.getAriaLabel(color)}
      >
        <div
          className={'tp-filter-list-item'}
          tabIndex={0}
          role={'link'}
          onClick={this.handleFilterClick}
          onKeyDown={this.handleEnter}
        >
          <Container
            displayFlex
            className='tp-filter-list-card tp-100'
          >
            {this.renderSearchNameWithCount()}
            <Container
              displayFlex
              justifyContentBetween
              className="tp-filter-list-item-settings tp-100"
            >
              {this.supplierView ?
                <div className={paletteIndicator} style={{ backgroundColor: colorRgbString }} /> :
                <>
                  <ColorPicker
                    open={this.state.showColorPalette}
                    paletteSettings={this.paletteSettings}
                    defaultValue={colorRgbString}
                    onBlur={() => this.setState({ showColorPalette: false })}
                    onChange={this.onColorPickerChange}
                  />
                  <PuzzleButton
                    aria-label={this.translate('SRL-pickColor')}
                    className={paletteIndicator}
                    onClick={this.openColorPalette}
                    style={{ backgroundColor: colorRgbString }}
                  >
                    <i className={'tp-icon tp-arrow-right'} aria-hidden="true" />
                  </PuzzleButton>
                </>
              }
              {this.renderActions()}
            </Container>
          </Container>
        </div>
      </Container>
    );
  }

  /**
   * Handles click on filter box
   * Has to be overridden in inherited class
   *
   * @param event
   */
  public handleFilterClick = (event: any) => {
    console.error('this method has to be overridden')
  };
}

export default FilterListItemAbstract;
